import { useAtomValue } from 'jotai';
import { filterByAtom, searchTermsAtom } from '@/atoms/filterByAtom';
import { getFilterConfig } from '@/components/TypesenseFilters/filterConfigs';
import { parseFilterBy } from '@music-vine/setlist';

export const useTypesenseFilterParams = ({
  preSelectedFilter,
}:
  | {
      preSelectedFilter?: string;
    }
  | undefined = {}) => {
  const filterBy = useAtomValue(filterByAtom);
  const searchTerms = useAtomValue(searchTermsAtom);

  const { search, filter: parsedFilter } = parseFilterBy(filterBy);

  // Split both user filters and preselected filters
  const userFilters = parsedFilter?.split(' && ') ?? [];
  const preSelectedFilters = preSelectedFilter?.split(' && ') ?? [];

  // Categorize user filters
  const userFiltersByCategory: Record<string, string[]> = {};

  userFilters.forEach((filter) => {
    const [category] = filter.split(':');

    if (!userFiltersByCategory[category]) {
      userFiltersByCategory[category] = [];
    }

    userFiltersByCategory[category].push(filter);
  });

  // Process preselected filters, replacing with user filters where appropriate
  const finalFilters: string[] = [];

  preSelectedFilters.forEach((preFilter) => {
    const [category] = preFilter.split(':');
    const config = getFilterConfig(category);

    if (config.behavior === 'replace' && userFiltersByCategory[category]) {
      // Skip preselected filter - it will be replaced by user filter
      return;
    }

    // Keep preselected filter
    finalFilters.push(preFilter);
  });

  // Add all user filters
  Object.values(userFiltersByCategory).forEach((categoryFilters) => {
    const category = categoryFilters[0].split(':')[0];
    const config = getFilterConfig(category);

    if (config.logic === 'or' && categoryFilters.length > 1) {
      // Combine with OR logic
      finalFilters.push(`(${categoryFilters.join(' || ')})`);
    } else {
      // Add filters individually (AND logic)
      finalFilters.push(...categoryFilters);
    }
  });

  const filter = finalFilters.filter(Boolean).join(' && ').trim();

  return {
    search: searchTerms.length > 0 ? searchTerms : search, // Prefer atom-based search terms
    filter,
  };
};
